/**
 * Personas Configuration Page
 *
 * Manage persona generation settings:
 * - Default generation mode selector
 * - Sensitivity mode selector
 * - Refresh settings
 * - TTL settings
 * - Auto-generate toggle
 */

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Save, RefreshCw, AlertCircle, CheckCircle, ChevronDown } from 'lucide-react'
import {
  usePersonaConfiguration,
  useUpdatePersonaConfiguration,
  GenerationMode,
  SensitivityMode,
} from '@/hooks/usePersonas'
import PersonaModeSelector from '@/components/personas/PersonaModeSelector'
import EngineSelector from '@/components/persona-engine/EngineSelector'

export default function PersonasConfiguration() {
  const { data: config, isLoading } = usePersonaConfiguration()
  const updateConfig = useUpdatePersonaConfiguration()

  // Local form state
  const [defaultMode, setDefaultMode] = useState<GenerationMode>('full')
  const [sensitivityMode, setSensitivityMode] = useState<SensitivityMode>('full')
  const [refreshOnChange, setRefreshOnChange] = useState(false)
  const [ttlHours, setTtlHours] = useState(24)
  const [autoGenerate, setAutoGenerate] = useState(false)
  const [showEngineSection, setShowCPESection] = useState(false)

  // Track if form has changes
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Initialize form from config
  useEffect(() => {
    if (config) {
      setDefaultMode(config.default_generation_mode)
      setSensitivityMode(config.sensitivity_mode)
      setRefreshOnChange(config.refresh_on_profile_change)
      setTtlHours(config.ttl_hours)
      setAutoGenerate(config.auto_generate)
    }
  }, [config])

  // Track changes
  useEffect(() => {
    if (config) {
      const changed =
        defaultMode !== config.default_generation_mode ||
        sensitivityMode !== config.sensitivity_mode ||
        refreshOnChange !== config.refresh_on_profile_change ||
        ttlHours !== config.ttl_hours ||
        autoGenerate !== config.auto_generate
      setHasChanges(changed)
    }
  }, [config, defaultMode, sensitivityMode, refreshOnChange, ttlHours, autoGenerate])

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({
        default_generation_mode: defaultMode,
        sensitivity_mode: sensitivityMode,
        refresh_on_profile_change: refreshOnChange,
        ttl_hours: ttlHours,
        auto_generate: autoGenerate,
      })
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to save configuration:', error)
    }
  }

  const handleReset = () => {
    if (config) {
      setDefaultMode(config.default_generation_mode)
      setSensitivityMode(config.sensitivity_mode)
      setRefreshOnChange(config.refresh_on_profile_change)
      setTtlHours(config.ttl_hours)
      setAutoGenerate(config.auto_generate)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-personic-600" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <Link to="/tenant/personas" className="hover:text-personic-600">
            Personas
          </Link>
          <span>/</span>
          <span>Configuration</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900">Persona Configuration</h2>
        <p className="text-gray-600 mt-1">
          Configure default settings for persona generation
        </p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
          <CheckCircle className="w-5 h-5" />
          <span>Configuration saved successfully!</span>
        </div>
      )}

      {/* Error Message */}
      {updateConfig.isError && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>Failed to save configuration. Please try again.</span>
        </div>
      )}

      {/* Default Generation Mode */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Default Generation Mode
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Choose the default level of detail for generated personas. This can be
          overridden per-profile.
        </p>
        <PersonaModeSelector value={defaultMode} onChange={setDefaultMode} />
      </div>

      {/* Sensitivity Mode */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Sensitivity Mode
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          Control how sensitive signals are handled in persona generation.
        </p>
        <div className="space-y-3">
          {[
            {
              value: 'full' as SensitivityMode,
              label: 'Full Access',
              description:
                'All signals available, requires appropriate attestations for regulated data',
            },
            {
              value: 'partial' as SensitivityMode,
              label: 'Filtered',
              description:
                'Sensitive signals masked with neutral defaults, functional dimensions available',
            },
            {
              value: 'minimal' as SensitivityMode,
              label: 'Maximum Privacy',
              description:
                'Only communication style available, all personality data excluded',
            },
          ].map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                sensitivityMode === option.value
                  ? 'border-personic-500 bg-personic-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="sensitivity-mode"
                value={option.value}
                checked={sensitivityMode === option.value}
                onChange={() => setSensitivityMode(option.value)}
                className="mt-1 h-4 w-4 text-personic-600 focus:ring-personic-500 border-gray-300"
              />
              <div>
                <span className="font-medium text-gray-900">{option.label}</span>
                <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Refresh Settings */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Refresh Settings
        </h3>

        {/* Auto-refresh on profile change */}
        <label className="flex items-start gap-4 mb-6">
          <input
            type="checkbox"
            checked={refreshOnChange}
            onChange={(e) => setRefreshOnChange(e.target.checked)}
            className="mt-1 h-5 w-5 text-personic-600 focus:ring-personic-500 border-gray-300 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">
              Auto-refresh on profile changes
            </span>
            <p className="text-sm text-gray-500 mt-0.5">
              Automatically regenerate personas when their source profiles are updated
              with new signals
            </p>
          </div>
        </label>

        {/* TTL Setting */}
        <div>
          <label className="block font-medium text-gray-900 mb-2">
            Persona TTL (Time to Live)
          </label>
          <p className="text-sm text-gray-500 mb-3">
            How long before personas are considered stale and need regeneration
          </p>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={720}
              value={ttlHours}
              onChange={(e) => setTtlHours(Math.max(1, parseInt(e.target.value) || 24))}
              className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
            />
            <span className="text-gray-600">hours</span>
            <div className="flex gap-2 ml-4">
              {[24, 48, 168, 720].map((hours) => (
                <button
                  key={hours}
                  onClick={() => setTtlHours(hours)}
                  className={`px-3 py-1 text-sm rounded-md transition-colors ${
                    ttlHours === hours
                      ? 'bg-personic-100 text-personic-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {hours < 168
                    ? `${hours}h`
                    : `${hours / 168}w`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Auto-generate */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Automatic Generation
        </h3>
        <label className="flex items-start gap-4">
          <input
            type="checkbox"
            checked={autoGenerate}
            onChange={(e) => setAutoGenerate(e.target.checked)}
            className="mt-1 h-5 w-5 text-personic-600 focus:ring-personic-500 border-gray-300 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">
              Auto-generate personas for new profiles
            </span>
            <p className="text-sm text-gray-500 mt-0.5">
              Automatically generate a persona when a new profile reaches minimum
              maturity (4+ observations)
            </p>
          </div>
        </label>
      </div>

      {/* Advanced: Persona Engine (collapsible) */}
      <div className="card overflow-hidden">
        <button
          onClick={() => setShowCPESection(!showEngineSection)}
          className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
        >
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">
              Advanced: Persona Engine
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              Configure the Persona Engine used for persona generation
            </p>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform ${
              showEngineSection ? 'rotate-180' : ''
            }`}
          />
        </button>

        {showEngineSection && (
          <div className="px-6 pb-6 border-t border-gray-100 space-y-6">
            <div className="pt-4">
              <p className="text-sm text-gray-500 mb-4">
                Quick engine selection. For full configuration including modifiers and thresholds,{' '}
                <Link
                  to="/tenant/engage/settings"
                  className="text-personic-600 hover:text-personic-700 font-medium"
                >
                  open Engage Settings &rarr; Persona Engine
                </Link>
              </p>
            </div>

            {/* Simplified Engine Selector */}
            <EngineSelector
              value="auto"
              onChange={() => {}}
              productLens="engage"
              disabled
            />

            {/* Injection Profile Quick Toggle */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Injection Profile</h4>
              <div className="flex gap-3">
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-personic-100 text-personic-700 border border-personic-200">
                  Core (default)
                </span>
                <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm text-gray-500 border border-gray-200">
                  Full
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Manage injection profile in Engage Settings
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Compliance Notice */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">Compliance Note</h4>
            <p className="text-sm text-amber-700 mt-1">
              These settings apply globally to your tenant. For regulated industries
              (healthcare, legal, finance, education), ensure your sensitivity mode
              aligns with your compliance requirements. Contact support if you need
              custom configuration.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4">
        <button
          onClick={handleReset}
          disabled={!hasChanges}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          disabled={!hasChanges || updateConfig.isPending}
          className="flex items-center gap-2 px-6 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {updateConfig.isPending ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  )
}
